import { useContext, useMemo } from 'react'
import { useUser } from './useUser'
import { useRmnBrand } from '../layouts/RmnBrand/RmnBrandContext'
import { isBrand, isBrandSales } from '../utils'
import { brandIsNetwork, isBrandPlan, BrandPlan } from '../utils/plans'
import { portalBasePath } from '../utils/portalPath'
import { PortalScopeContext } from './PortalScopeContext'

// Which brand the network/catalog pages operate on, and where their links
// live. Inside the brand-portal workspace (/portal/:brandSlug/*, any role —
// internal staff included) the resolved slug brand wins and every network
// link is prefixed with basePath; outside it, the logged-in user's own brand
// (brand[0] — the positional choice the pages have always made) with
// slug-free links. Keys on the context's `brand`, never `loading`: outside
// RmnBrandLayout the context default is { brand: null, loading: true }.
export interface NetworkScope {
  brandId: number
  brandName: string
  brandLogo: string | null
  brandSlug: string | null
  // The brand is a network root — plan enterprise_rmn | enterprise_agency,
  // i.e. the API's derived rmn flag (Catalog > Brands, supplier scoping).
  isNetwork: boolean
  // Subscription plan of the scoped brand (null when unknown).
  plan: BrandPlan | null
  // '' outside the workspace, `/portal/${slug}` inside — prefix every
  // network page link with it.
  basePath: string
  inWorkspace: boolean
  // The retailer's domain, e.g. "samsclub.com" — site_url's host, else the
  // first allowed crawl domain. null when the brand carries neither (the
  // Catalog copy then falls back to generic wording).
  domain: string | null
  // Per-user RMN allowlists from /me (internal Brand > Team). null = full
  // access on that axis. Cosmetic: pages hide Add/Import actions that the
  // API now 403s for restricted users; the lists themselves are already
  // server-filtered.
  permittedBrandIds: number[] | null
  permittedCategoryIds: number[] | null
}

// The user's allowlists apply to one network; inside a workspace they only
// count when that workspace IS the user's network (internal staff carry
// none, so they are never restricted).
const permittedFor = (user: any, networkBrandId: number | null) => {
  const p = user && user.rmn_permissions
  if (!p || !p.rmn_brand_id) return { permittedBrandIds: null, permittedCategoryIds: null }
  if (networkBrandId !== null && Number(networkBrandId) !== Number(p.rmn_brand_id)) {
    return { permittedBrandIds: null, permittedCategoryIds: null }
  }
  return {
    permittedBrandIds: Array.isArray(p.brand_ids) && p.brand_ids.length ? p.brand_ids : null,
    permittedCategoryIds:
      Array.isArray(p.rmn_category_ids) && p.rmn_category_ids.length ? p.rmn_category_ids : null,
  }
}

// Mirrors buildRmnLens's retailerDomain derivation in the API.
const retailerDomain = (b: any): string | null => {
  const raw = String((b && b.site_url) || '').trim()
  if (raw) {
    try {
      const withScheme = /^[a-z]+:\/\//i.test(raw) ? raw : `https://${raw}`
      const host = new URL(withScheme).hostname.replace(/^www\./i, '')
      if (host) return host
    } catch {
      // fall through to allowed_domains
    }
  }
  const allowed = b && Array.isArray(b.allowed_domains) ? b.allowed_domains : []
  return allowed.length ? String(allowed[0]).replace(/^www\./i, '') : null
}

export function useNetworkScope(): NetworkScope {
  const { brand: networkBrand } = useRmnBrand()
  const { user, brand, role } = useUser()
  // Declared by <PortalScope basePath=""> around the public/share branch of the
  // route table; wins over everything else so a logged-in brand user doesn't
  // prefix links on a page that has no portal routes.
  const scopeOverride = useContext(PortalScopeContext)
  return useMemo<NetworkScope>(() => {
    const scope: NetworkScope = networkBrand
      ? {
          brandId: networkBrand.id,
          brandName: networkBrand.name,
          brandLogo: networkBrand.logo || null,
          brandSlug: networkBrand.slug,
          isNetwork: brandIsNetwork(networkBrand),
          plan: isBrandPlan(networkBrand.plan) ? networkBrand.plan : null,
          basePath: portalBasePath(networkBrand.slug),
          inWorkspace: true,
          domain: retailerDomain(networkBrand),
          ...permittedFor(user, networkBrand.id),
        }
      : (() => {
          const own: any = Array.isArray(brand) && brand[0] ? brand[0] : null
          // Network posture follows ANY of the user's brands (a supplier user
          // whose membership includes the network root) — same rule isRMN
          // in utils applies.
          const ownNetwork: any = Array.isArray(brand) ? brand.find(brandIsNetwork) : null
          // A brand client's portal is their OWN brand, wherever in the app
          // they are (TDC-1609) — that one line is what moves every existing
          // basePath consumer, the rail and the link sweep onto slugged URLs.
          // A client belongs to exactly one brand, so brand[0] is unambiguous.
          // Internal staff, experts and pros keep '' here and only pick up a
          // basePath inside a workspace, via the branch above.
          const clientBase =
            (isBrand(role) || isBrandSales(role)) && own && own.slug
              ? portalBasePath(own.slug)
              : ''
          return {
            brandId: own ? own.id : 0,
            brandName: own ? own.name || '' : '',
            brandLogo: own ? own.logo || null : null,
            brandSlug: own ? own.slug || null : null,
            isNetwork: !!ownNetwork,
            plan: isBrandPlan(ownNetwork ? ownNetwork.plan : own && own.plan)
              ? (ownNetwork ? ownNetwork.plan : own.plan)
              : null,
            basePath: clientBase,
            inWorkspace: false,
            domain: retailerDomain(own),
            ...permittedFor(user, null),
          }
        })()

    // A declared scope (the public/share branch) overrides the derived one.
    if (scopeOverride) {
      return { ...scope, basePath: scopeOverride.basePath, inWorkspace: false }
    }
    return scope
  }, [networkBrand, brand, user, role, scopeOverride])
}
